import type { FC, ReactNode } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import Tooltip from '@components/Tooltip';
import clsx from 'clsx';

import { selectAvailableTabs, selectIsInSplitMode, usePDFPreviewStore } from '../store/pdf-preview.store';

import { documentFileNameTabClasses, fileNameTabContainerClasses } from './classes';

interface TabButtonProps {
  className?: string;
  content: ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const TabButton: FC<TabButtonProps> = ({ className, content, isActive, onClick }) => {
  return (
    <button className={fileNameTabContainerClasses({ isActive, className })} onClick={onClick}>
      <ShowFirstMatching fallback={content}>
        <Show when={typeof content === 'string'}>
          <p className="truncate text-xs leading-5 font-normal">{content}</p>
        </Show>
      </ShowFirstMatching>
    </button>
  );
};

interface TabSkeletonProps {
  shouldShowVersionBadge: boolean;
}

const TabSkeleton: FC<TabSkeletonProps> = ({ shouldShowVersionBadge }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 rounded-md bg-gray-200" />
      <Show when={shouldShowVersionBadge}>
        <div className="h-4 w-4 shrink-0 rounded-full bg-gray-200" />
      </Show>
    </div>
  );
};

interface VersionBadgeProps {
  version?: number;
}

const VersionBadge: FC<VersionBadgeProps> = ({ version }) => {
  if (version === undefined) return null;

  return (
    <Tooltip content={<div>PDF Save no. {version}</div>} placement="bottom">
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200">
        <span className="text-medium text-xs text-gray-800" data-testid="pdf-document-version">
          {version}
        </span>
      </div>
    </Tooltip>
  );
};

interface PDFPreviewTabsProps {
  className?: string;
  isLoading: boolean;
}

export const PDFPreviewTabs: FC<PDFPreviewTabsProps> = ({ className, isLoading }) => {
  const { activeTabId, setActiveTab, availableTabs, isInSplitMode } = usePDFPreviewStore((state) => ({
    activeTabId: state.activeTabId,
    setActiveTab: state.setActiveTab,
    availableTabs: selectAvailableTabs(state),
    isInSplitMode: selectIsInSplitMode(state),
  }));

  const tabsToShow = isInSplitMode ? availableTabs.filter((t) => t.id === activeTabId) : availableTabs;
  const showFullMargin = isInSplitMode || availableTabs.length === 1;

  return (
    <div className={className}>
      {tabsToShow.map((tab, index) => {
        const shouldShowVersionBadge = !(tab.shouldHideVersionBadge ?? false);
        return (
          <TabButton
            key={tab.id}
            className={clsx(
              documentFileNameTabClasses({
                marginFull: showFullMargin || index === tabsToShow.length - 1,
              }),
              'min-w-0',
            )}
            content={
              isLoading ? (
                <TabSkeleton shouldShowVersionBadge={shouldShowVersionBadge} />
              ) : (
                <>
                  <p className="truncate text-xs leading-5 font-normal">{tab.label}</p>
                  <Show when={shouldShowVersionBadge}>
                    <VersionBadge version={tab.documentVersion} />
                  </Show>
                </>
              )
            }
            isActive={tab.id === activeTabId}
            onClick={() => setActiveTab(tab.id)}
          />
        );
      })}
    </div>
  );
};
